import { LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { NotesComponent } from './notes/notes.component';
import { SummaryComponent } from './summary/summary.component';
import { MenuComponent } from './menu/menu.component';
import { ProductListComponent } from './catalog/product/product-list/product-list.component';
import { CategoryListComponent } from './catalog/category/category-list/category-list.component';
import { CatalogComponent } from './catalog/catalog.component';
import { ProductDetailComponent } from './catalog/product/product-detail/product-detail.component';
import { ProductService } from './api/catalog/product.service';
import AlertifyHelper from './utils/utils';
import { LoadingComponent } from './components/loading/loading.component';
import { PaginationComponent } from './components/pagination/pagination.component';
import { NgxPaginationModule } from 'ngx-pagination';
import { ProductFormComponent } from './catalog/product/product-form/product-form.component';
import { NumericDirective } from './directives/numeric.directive';
import { LoginComponent } from './auth/login/login.component';
import { BranchofficeListComponent } from './admin/branchoffice/branchoffice-list/branchoffice-list.component';
import { AdminComponent } from './admin/admin.component';
import { AuthGuard } from './guards/auth.guard';
import { MainSysComponent } from './main-sys/main-sys.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { PageNotfoundComponent } from './errors/page-notfound/page-notfound.component';
import { SearchComponent } from './components/search/search.component';
import { RouterModule } from '@angular/router';
import { CategoryFormComponent } from './catalog/category/category-form/category-form.component';
import { AutofocusDirective } from './directives/autofocus.directive';
import { AllowUppercaseDirective } from './directives/allow-uppercase.directive';
import { HasPermissionsDirective } from './directives/has-permissions.directive';
import { NavigationTrackerService } from './utils/navigation-tracker.service';
import { CitietouristicListComponent } from './tourism/citietouristic/citietouristic-list/citietouristic-list.component';
import { CitietouristicFormComponent } from './tourism/citietouristic/citietouristic-form/citietouristic-form.component';
import { TourListComponent } from './tourism/tour/tour-list/tour-list.component';
import { TourFormComponent } from './tourism/tour/tour-form/tour-form.component';
import { HotelFormComponent } from './tourism/hotel/hotel-form/hotel-form.component';
import { HotelListComponent } from './tourism/hotel/hotel-list/hotel-list.component';
import { PackagetourListComponent } from './tourism/packagetour/packagetour-list/packagetour-list.component';
import { PackagetourFormComponent } from './tourism/packagetour/packagetour-form/packagetour-form.component';
import { CKEditorModule } from 'ckeditor4-angular';
import { TypebedroomExtendsComponent } from './config/typebedroom/typebedroom-extends/typebedroom-extends.component';
import { TypebedroomListComponent } from './config/typebedroom/typebedroom-list/typebedroom-list.component';
import { TypebedroomFormComponent } from './config/typebedroom/typebedroom-form/typebedroom-form.component';
import { ServicehotelFormComponent } from './config/servicehotel/servicehotel-form/servicehotel-form.component';
import { ServicehotelExtendsComponent } from './config/servicehotel/servicehotel-extends/servicehotel-extends.component';
import { LoadingModalComponent } from './components/loading-modal/loading-modal.component';
import { NumericDecimalsDirective } from './directives/numeric-decimals.directive';
import { HotelMenutabComponent } from './tourism/hotel/hotel-menutab/hotel-menutab.component';
import { HotelGalleryImgComponent } from './tourism/hotel/hotel-gallery-img/hotel-gallery-img.component';
import { HotelGalleryVideoComponent } from './tourism/hotel/hotel-gallery-video/hotel-gallery-video.component';
import { SafePipe } from './pipes/safe';
import { TourGalleryImgComponent } from './tourism/tour/tour-gallery-img/tour-gallery-img.component';
import { TourGalleryVideoComponent } from './tourism/tour/tour-gallery-video/tour-gallery-video.component';
import { TourMenutabComponent } from './tourism/tour/tour-menutab/tour-menutab.component';
import { InnerHTMLPipe } from './pipes/inner-html.pipe';
import { PackagetourMenutabComponent } from './tourism/packagetour/packagetour-menutab/packagetour-menutab.component';
import { PackagetourGalleryImgComponent } from './tourism/packagetour/packagetour-gallery-img/packagetour-gallery-img.component';
import { PackagetourGalleryVideoComponent } from './tourism/packagetour/packagetour-gallery-video/packagetour-gallery-video.component';
import { AutocompleteLibModule } from 'angular-ng-autocomplete';
import { SearchTourComponent } from './components/search-tour/search-tour.component';
import { SearchHotelComponent } from './components/search-hotel/search-hotel.component';
import { GalleryTourismComponent } from './components/gallery-tourism/gallery-tourism.component';
import { NgSelectModule } from '@ng-select/ng-select';
import { VideoTourismComponent } from './components/video-tourism/video-tourism.component';
import { ClientListComponent } from './sale/client/client-list/client-list.component';
import { ClientFormComponent } from './sale/client/client-form/client-form.component';
import { SalesListComponent } from './sale/sales/sales-list/sales-list.component';
import { SalesFormComponent } from './sale/sales/sales-form/sales-form.component';
import { BookingsFormComponent } from './booking/bookings/bookings-form/bookings-form.component';
import { BookingsDetailComponent } from './booking/bookings/bookings-detail/bookings-detail.component';
import { BookingComponent } from './booking/booking.component';
import { BookingsListComponent } from './booking/bookings/bookings-list/bookings-list.component';
import { SearchClientComponent } from './components/search-client/search-client.component';
import { ClientExtendsComponent } from './sale/client/client-extends/client-extends.component';
import { BookingsHotelExtendsComponent } from './booking/bookings/bookings-hotel-extends/bookings-hotel-extends.component';
import { SearchEmployeeComponent } from './components/search-employee/search-employee.component';
import { OpeningListComponent } from './money/opening/opening-list/opening-list.component';
import { IncomeListComponent } from './money/income/income-list/income-list.component';
import { ExpenseListComponent } from './money/expense/expense-list/expense-list.component';
import { MoneyComponent } from './money/money.component';
import { IncomeFormComponent } from './money/income/income-form/income-form.component';
import { ExpenseFormComponent } from './money/expense/expense-form/expense-form.component';
import { PurchaseComponent } from './purchase/purchase.component';
import { ProviderListComponent } from './purchase/provider/provider-list/provider-list.component';
import { ProviderFormComponent } from './purchase/provider/provider-form/provider-form.component';
import { ProviderExtendsComponent } from './purchase/provider/provider-extends/provider-extends.component';
import { SearchProviderComponent } from './components/search-provider/search-provider.component';
import { BrandFormComponent } from './catalog/brand/brand-form/brand-form.component';
import { BrandListComponent } from './catalog/brand/brand-list/brand-list.component';
import { BrandExtendsComponent } from './catalog/brand/brand-extends/brand-extends.component';
import { LineListComponent } from './catalog/line/line-list/line-list.component';
import { LineFormComponent } from './catalog/line/line-form/line-form.component';
import { ServiceFormComponent } from './catalog/service/service-form/service-form.component';
import { ServiceListComponent } from './catalog/service/service-list/service-list.component';
import { BranchofficeGuard } from './guards/branchoffice.guard';
import { SaleComponent } from './sale/sale.component';
import { SalesDetailComponent } from './sale/sales/sales-detail/sales-detail.component';
import { ProductPriceunitsExtendsComponent } from './catalog/product/product-priceunits-extends/product-priceunits-extends.component';
import { ProductInformationdetailExtendsComponent } from './catalog/product/product-informationdetail-extends/product-informationdetail-extends.component';
import { WarehouseComponent } from './warehouse/warehouse.component';
import { EntryListComponent } from './warehouse/entry/entry-list/entry-list.component';
import { EntryFormComponent } from './warehouse/entry/entry-form/entry-form.component';
import { OutputFormComponent } from './warehouse/output/output-form/output-form.component';
import { OutputListComponent } from './warehouse/output/output-list/output-list.component';
import { OutputDetailComponent } from './warehouse/output/output-detail/output-detail.component';
import { EntryDetailComponent } from './warehouse/entry/entry-detail/entry-detail.component';
import { StockgeneralListComponent } from './warehouse/stockgeneral/stockgeneral-list/stockgeneral-list.component';
import { SearchProductComponent } from './components/search-product/search-product.component';
import { DepartureComponent } from './departure/departure.component';
import { ScheduleListComponent } from './departure/schedule/schedule-list/schedule-list.component';
import { GroupListComponent } from './departure/group/group-list/group-list.component';
import { GroupFormComponent } from './departure/group/group-form/group-form.component';
import { ScheduleDetailComponent } from './departure/schedule/schedule-detail/schedule-detail.component';
import { BookingsDetailExtendsComponent } from './booking/bookings/bookings-detail-extends/bookings-detail-extends.component';
import { BookingsAmortizationExtendsComponent } from './booking/bookings/bookings-amortization-extends/bookings-amortization-extends.component';
import { BookingsCommissionExtendsComponent } from './booking/bookings/bookings-commission-extends/bookings-commission-extends.component';
import { CollaboratorComponent } from './collaborator/collaborator.component';
import { CollaboratorsListComponent } from './collaborator/collaborators/collaborators-list/collaborators-list.component';
import { CollaboratorsFormComponent } from './collaborator/collaborators/collaborators-form/collaborators-form.component';
import { PartnersFormComponent } from './collaborator/partners/partners-form/partners-form.component';
import { PartnersListComponent } from './collaborator/partners/partners-list/partners-list.component';
import { SearchCollaboratorsComponent } from './components/search-collaborators/search-collaborators.component';
import { FinanceComponent } from './finance/finance.component';
import { BankListComponent } from './finance/bank/bank-list/bank-list.component';
import { BankFormComponent } from './finance/bank/bank-form/bank-form.component';
import { AccountbankFormComponent } from './finance/accountbank/accountbank-form/accountbank-form.component';
import { AccountbankListComponent } from './finance/accountbank/accountbank-list/accountbank-list.component';
import { ScheduleOpenComponent } from './departure/schedule/schedule-open/schedule-open.component';
import { TransportListComponent } from './tourism/transport/transport-list/transport-list.component';
import { TransportFormComponent } from './tourism/transport/transport-form/transport-form.component';
import { AgenciesListComponent } from './tourism/agencies/agencies-list/agencies-list.component';
import { AgenciesFormComponent } from './tourism/agencies/agencies-form/agencies-form.component';
import { GuidesFormComponent } from './tourism/guides/guides-form/guides-form.component';
import { GuidesListComponent } from './tourism/guides/guides-list/guides-list.component';
import { SearchTransportComponent } from './components/search-transport/search-transport.component';
import { SearchGuidesComponent } from './components/search-guides/search-guides.component';
import { GroupDetailComponent } from './departure/group/group-detail/group-detail.component';
import { TransfersListComponent } from './booking/transfers/transfers-list/transfers-list.component';
import { TransfersFormComponent } from './booking/transfers/transfers-form/transfers-form.component';
import { TransfersDetailComponent } from './booking/transfers/transfers-detail/transfers-detail.component';
import { ReceivedsListComponent } from './booking/receiveds/receiveds-list/receiveds-list.component';
import { ReceivedsFormComponent } from './booking/receiveds/receiveds-form/receiveds-form.component';
import { ReceivedsDetailComponent } from './booking/receiveds/receiveds-detail/receiveds-detail.component';
import { SearchBookingsComponent } from './components/search-bookings/search-bookings.component';
import { SearchAgenciesComponent } from './components/search-agencies/search-agencies.component';
import { TransfersExtendsComponent } from './booking/transfers/transfers-extends/transfers-extends.component';
import { ArrivalsListComponent } from './booking/arrivals/arrivals-list/arrivals-list.component';
import { CollaboratorsSelectExtendsComponent } from './collaborator/collaborators/collaborators-select-extends/collaborators-select-extends.component';
import { HighchartsChartModule } from 'highcharts-angular';
import { SearchPackagetourComponent } from './components/search-packagetour/search-packagetour.component';
import { HotelInformationExtendsComponent } from './tourism/hotel/hotel-information-extends/hotel-information-extends.component';
import { ReportComponent } from './report/report.component';
import { ReportListComponent } from './report/report-list/report-list.component';
import { BlockshotelComponent } from './report/blockshotel/blockshotel.component';
import { BlockshotelFormComponent } from './report/blockshotel/blockshotel-form/blockshotel-form.component';
import { BlocksagencyComponent } from './report/blocksagency/blocksagency.component';
import { BlocksagencyFormComponent } from './report/blocksagency/blocksagency-form/blocksagency-form.component';
import { BlockshotelDetailComponent } from './report/blockshotel/blockshotel-detail/blockshotel-detail.component';
import { BlocksagencyDetailComponent } from './report/blocksagency/blocksagency-detail/blocksagency-detail.component';
import { ScheduleReportComponent } from './report/departure/schedule-report/schedule-report.component';
import { GroupReportComponent } from './report/departure/group-report/group-report.component';
import { CotizationListComponent } from './tourism/cotization/cotization-list/cotization-list.component';
import { CotizationFormComponent } from './tourism/cotization/cotization-form/cotization-form.component';
import { BookingsExtendsComponent } from './booking/bookings/bookings-extends/bookings-extends.component';
import { GroupDetailExtendsComponent } from './departure/group/group-detail-extends/group-detail-extends.component';
import { HistoryboxListComponent } from './money/historybox/historybox-list/historybox-list.component';
import { NavtopBranchofficeInformationComponent } from './components/navtop-branchoffice-information/navtop-branchoffice-information.component';
import { RemoveSpacesNumberDirective } from './directives/remove-spaces-number.directive';
import { VoucherListComponent } from './billing/voucher/voucher-list/voucher-list.component';
import { BillingComponent } from './billing/billing.component';
import { SummaryAdviserComponent } from './report/bookings/summary-adviser/summary-adviser.component';
import { ScheduleProjectionComponent } from './departure/schedule/schedule-projection/schedule-projection.component';
import { OfferListComponent } from './tourism/offer/offer-list/offer-list.component';
import { OfferFormComponent } from './tourism/offer/offer-form/offer-form.component';
import { CategorypackagetourExtendsComponent } from './config/categorypackagetour/categorypackagetour-extends/categorypackagetour-extends.component';
import { VehicletypeExtendsComponent } from './config/vehicletype/vehicletype-extends/vehicletype-extends.component';
import { VehicletypeListComponent } from './config/vehicletype/vehicletype-list/vehicletype-list.component';
import { ConceptIncomeExtendsComponent } from './config/concept-income/concept-income-extends/concept-income-extends.component';
import { ConceptExpenseExtendsComponent } from './config/concept-expense/concept-expense-extends/concept-expense-extends.component';
import { HistoryboxDetailComponent } from './money/historybox/historybox-detail/historybox-detail.component';
import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import { UploadFileMovementsExtendsComponent } from './components/upload-file-movements-extends/upload-file-movements-extends.component';
import { GroupReportBalanceComponent } from './report/departure/group-report/group-report-balance/group-report-balance.component';
import { VoucherCancelledExtendsComponent } from './billing/voucher/voucher-cancelled-extends/voucher-cancelled-extends.component';
import { VoucherFormComponent } from './billing/voucher/voucher-form/voucher-form.component';
import { GroupGuideProjectionComponent } from './departure/group/group-guide-projection/group-guide-projection.component';
import { ImageCropperModule } from 'ngx-image-cropper';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { ServicehotelListComponent } from './config/servicehotel/servicehotel-list/servicehotel-list.component';
import { FlightComponent } from './flight/flight.component';
import { FlightsListComponent } from './flight/flights/flights-list/flights-list.component';
import { FlightsFormComponent } from './flight/flights/flights-form/flights-form.component';
import { AirlinesListComponent } from './flight/airlines/airlines-list/airlines-list.component';
import { AirlinesFormComponent } from './flight/airlines/airlines-form/airlines-form.component';
import { BookingflightsListComponent } from './flight/bookingflights/bookingflights-list/bookingflights-list.component';
import { BookingflightsFormComponent } from './flight/bookingflights/bookingflights-form/bookingflights-form.component';
import { SearchAirportComponent } from './components/search-airport/search-airport.component';
import { BookingflightsService } from './api/flight/bookingflights.service';
import { BookingflightsDetailComponent } from './flight/bookingflights/bookingflights-detail/bookingflights-detail.component';
import { AmortizationExtendsComponent } from './components/amortization-extends/amortization-extends.component';
import { HospitalityComponent } from './hospitality/hospitality.component';
import { RoomtypeListComponent } from './hospitality/roomtype/roomtype-list/roomtype-list.component';
import { RoomtypeFormComponent } from './hospitality/roomtype/roomtype-form/roomtype-form.component';
import { RestaurantComponent } from './restaurant/restaurant.component';
import { CategoryfoodListComponent } from './restaurant/categoryfood/categoryfood-list/categoryfood-list.component';
import { CategoryfoodFormComponent } from './restaurant/categoryfood/categoryfood-form/categoryfood-form.component';
import { FoodsFormComponent } from './restaurant/foods/foods-form/foods-form.component';
import { FoodsListComponent } from './restaurant/foods/foods-list/foods-list.component';
import { BlogComponent } from './blog/blog.component';
import { PostsListComponent } from './blog/posts/posts-list/posts-list.component';
import { PostsFormComponent } from './blog/posts/posts-form/posts-form.component';
import { BlogcategoryFormComponent } from './blog/blogcategory/blogcategory-form/blogcategory-form.component';
import { BlogcategoryListComponent } from './blog/blogcategory/blogcategory-list/blogcategory-list.component';
import { AuthorListComponent } from './blog/author/author-list/author-list.component';
import { AuthorFormComponent } from './blog/author/author-form/author-form.component';
import { SearchAuthorComponent } from './components/search-author/search-author.component';
import { GuideReportComponent } from './report/departure/guide-report/guide-report.component';
import { TransportReportComponent } from './report/departure/transport-report/transport-report.component';
import { RecordGuideExtendsComponent } from './departure/group/record-guide-extends/record-guide-extends.component';
import { RecordTransportExtendsComponent } from './departure/group/record-transport-extends/record-transport-extends.component';
import { BedroomListComponent } from './hospitality/bedroom/bedroom-list/bedroom-list.component';
import { BedroomFormComponent } from './hospitality/bedroom/bedroom-form/bedroom-form.component';
import { RatesListComponent } from './hospitality/rates/rates-list/rates-list.component';
import { RatesFormComponent } from './hospitality/rates/rates-form/rates-form.component';
import { RatesPricesComponent } from './hospitality/rates/rates-prices/rates-prices.component';
import { AgreementsListComponent } from './collaborator/agreements/agreements-list/agreements-list.component';
import { AgreementsFormComponent } from './collaborator/agreements/agreements-form/agreements-form.component';
import { SearchPartnercompaniesComponent } from './components/search-partnercompanies/search-partnercompanies.component';
import { HotelbookingComponent } from './hotelbooking/hotelbooking.component';
import { HotelbookingsListComponent } from './hotelbooking/hotelbookings/hotelbookings-list/hotelbookings-list.component';
import { HotelbookingsFormComponent } from './hotelbooking/hotelbookings/hotelbookings-form/hotelbookings-form.component';
import { SearchAvailableBedroomsExtendsComponent } from './components/search-available-bedrooms-extends/search-available-bedrooms-extends.component';
import { GuestsExtendsComponent } from './hotelbooking/guests/guests-extends/guests-extends.component';
import { GuestsListComponent } from './hotelbooking/guests/guests-list/guests-list.component';
import { ProformaListComponent } from './sale/proforma/proforma-list/proforma-list.component';
import { ProformaFormComponent } from './sale/proforma/proforma-form/proforma-form.component';
import localeEsPe from '@angular/common/locales/es-PE';
import { FullCalendarModule } from '@fullcalendar/angular';
import { HoteltransfersListComponent } from './hotelbooking/hoteltransfers/hoteltransfers-list/hoteltransfers-list.component';
import { HotelplaningComponent } from './hotelbooking/hotelplaning/hotelplaning.component';
import { HotelbookingsDetailComponent } from './hotelbooking/hotelbookings/hotelbookings-detail/hotelbookings-detail.component';
import { ScheduleOpenListComponent } from './departure/schedule/schedule-open-list/schedule-open-list.component';
import { VouchersReportComponent } from './report/billing/vouchers-report/vouchers-report.component';
import { HotelbookingsExtendsComponent } from './hotelbooking/hotelbookings/hotelbookings-extends/hotelbookings-extends.component';
import { LoungesListComponent } from './restaurant/lounges/lounges-list/lounges-list.component';
import { LoungesFormComponent } from './restaurant/lounges/lounges-form/lounges-form.component';
import { LoungesTablesComponent } from './restaurant/lounges/lounges-tables/lounges-tables.component';
import { UserOtpComponent } from './auth/user-otp/user-otp.component';
import { CompanyListComponent } from './admin/enterprise/company/company-list/company-list.component';
import { CompanyInformationComponent } from './admin/enterprise/company/company-information/company-information.component';
import { EmployeesListComponent } from './admin/enterprise/employees/employees-list/employees-list.component';
import { EmployeesFormComponent } from './admin/enterprise/employees/employees-form/employees-form.component';
import { JobsFormComponent } from './admin/enterprise/jobs/jobs-form/jobs-form.component';
import { JobsListComponent } from './admin/enterprise/jobs/jobs-list/jobs-list.component';
import { EnterpriseComponent } from './admin/enterprise/enterprise.component';
import { UsersListComponent } from './admin/enterprise/users/users-list/users-list.component';
import { UsersFormComponent } from './admin/enterprise/users/users-form/users-form.component';

// registerLocaleData(localeEs, 'es');
registerLocaleData(localeEsPe, 'es-PE');

@NgModule({
  declarations: [
    AppComponent,
    NotesComponent,
    SummaryComponent,
    MenuComponent,
    ProductListComponent,
    CategoryListComponent,
    CategoryFormComponent,
    CatalogComponent,
    ProductDetailComponent,
    LoadingComponent,
    PaginationComponent,
    ProductFormComponent,
    NumericDirective,
    LoginComponent,
    BranchofficeListComponent,
    AdminComponent,
    MainSysComponent,
    DashboardComponent,
    PageNotfoundComponent,
    SearchComponent,
    AutofocusDirective,
    AllowUppercaseDirective,
    HasPermissionsDirective,
    CitietouristicListComponent,
    CitietouristicFormComponent,
    TourListComponent,
    TourFormComponent,
    HotelFormComponent,
    HotelListComponent,
    PackagetourListComponent,
    PackagetourFormComponent,
    TypebedroomExtendsComponent,
    TypebedroomListComponent,
    TypebedroomFormComponent,
    ServicehotelFormComponent,
    ServicehotelListComponent,
    ServicehotelExtendsComponent,
    LoadingModalComponent,
    NumericDecimalsDirective,
    HotelMenutabComponent,
    HotelGalleryImgComponent,
    HotelGalleryVideoComponent,
    SafePipe,
    TourGalleryImgComponent,
    TourGalleryVideoComponent,
    TourMenutabComponent,
    InnerHTMLPipe,
    PackagetourMenutabComponent,
    PackagetourGalleryImgComponent,
    PackagetourGalleryVideoComponent,
    SearchTourComponent,
    SearchHotelComponent,
    GalleryTourismComponent,
    VideoTourismComponent,
    ClientListComponent,
    ClientFormComponent,
    SalesListComponent,
    SalesFormComponent,
    BookingsListComponent,
    BookingsFormComponent,
    BookingsDetailComponent,
    BookingComponent,
    SearchClientComponent,
    ClientExtendsComponent,
    BookingsHotelExtendsComponent,
    SearchEmployeeComponent,
    MoneyComponent,
    OpeningListComponent,
    IncomeListComponent,
    ExpenseListComponent,
    IncomeFormComponent,
    ExpenseFormComponent,
    PurchaseComponent,
    ProviderListComponent,
    ProviderFormComponent,
    ProviderExtendsComponent,
    SearchProviderComponent,
    BrandFormComponent,
    BrandListComponent,
    BrandExtendsComponent,
    LineListComponent,
    LineFormComponent,
    ServiceFormComponent,
    ServiceListComponent,
    SaleComponent,
    SalesDetailComponent,
    ProductPriceunitsExtendsComponent,
    ProductInformationdetailExtendsComponent,
    WarehouseComponent,
    EntryListComponent,
    EntryFormComponent,
    OutputFormComponent,
    OutputListComponent,
    OutputDetailComponent,
    EntryDetailComponent,
    StockgeneralListComponent,
    SearchProductComponent,
    DepartureComponent,
    ScheduleListComponent,
    GroupListComponent,
    GroupFormComponent,
    ScheduleDetailComponent,

    BookingsAmortizationExtendsComponent,
    BookingsCommissionExtendsComponent,
    CollaboratorComponent,
    CollaboratorsListComponent,
    CollaboratorsFormComponent,
    PartnersFormComponent,
    PartnersListComponent,
    SearchCollaboratorsComponent,
    FinanceComponent,
    BankListComponent,
    BankFormComponent,
    AccountbankFormComponent,
    AccountbankListComponent,
    ScheduleOpenComponent,
    TransportListComponent,
    TransportFormComponent,
    AgenciesListComponent,
    AgenciesFormComponent,
    GuidesFormComponent,
    GuidesListComponent,
    SearchTransportComponent,
    SearchGuidesComponent,
    GroupDetailComponent,
    TransfersListComponent,
    TransfersFormComponent,
    TransfersDetailComponent,
    ReceivedsListComponent,
    ReceivedsFormComponent,
    ReceivedsDetailComponent,
    SearchBookingsComponent,
    SearchAgenciesComponent,
    TransfersExtendsComponent,
    ArrivalsListComponent,
    CollaboratorsSelectExtendsComponent,
    SearchPackagetourComponent,
    CompanyListComponent,
    BookingsDetailExtendsComponent,
    CompanyInformationComponent,
    EmployeesListComponent,
    EmployeesFormComponent,
    JobsFormComponent,
    JobsListComponent,
    EnterpriseComponent,
    HotelInformationExtendsComponent,
    ReportComponent,
    ReportListComponent,
    BlockshotelComponent,
    BlockshotelFormComponent,
    BlocksagencyComponent,
    BlocksagencyFormComponent,
    BlockshotelDetailComponent,
    BlocksagencyDetailComponent,
    UsersListComponent,
    UsersFormComponent,
    ScheduleReportComponent,
    GroupReportComponent,
    CotizationListComponent,
    CotizationFormComponent,
    BookingsExtendsComponent,
    GroupDetailExtendsComponent,
    HistoryboxListComponent,
    NavtopBranchofficeInformationComponent,
    RemoveSpacesNumberDirective,
    VoucherListComponent,
    BillingComponent,
    SummaryAdviserComponent,
    ScheduleProjectionComponent,
    OfferListComponent,
    OfferFormComponent,
    CategorypackagetourExtendsComponent,
    VehicletypeExtendsComponent,
    VehicletypeListComponent,
    ConceptIncomeExtendsComponent,
    ConceptExpenseExtendsComponent,
    HistoryboxDetailComponent,
    UploadFileMovementsExtendsComponent,
    GroupReportBalanceComponent,
    VoucherCancelledExtendsComponent,
    VoucherFormComponent,
    GroupGuideProjectionComponent,
    FlightComponent,
    FlightsListComponent,
    FlightsFormComponent,
    AirlinesListComponent,
    AirlinesFormComponent,
    BookingflightsListComponent,
    BookingflightsFormComponent,
    SearchAirportComponent,
    BookingflightsDetailComponent,
    AmortizationExtendsComponent,
    HospitalityComponent,
    RoomtypeListComponent,
    RoomtypeFormComponent,
    RestaurantComponent,
    CategoryfoodListComponent,
    CategoryfoodFormComponent,
    FoodsFormComponent,
    FoodsListComponent,
    BlogComponent,
    PostsListComponent,
    PostsFormComponent,
    BlogcategoryFormComponent,
    BlogcategoryListComponent,
    AuthorListComponent,
    AuthorFormComponent,
    SearchAuthorComponent,
    GuideReportComponent,
    TransportReportComponent,
    RecordGuideExtendsComponent,
    RecordTransportExtendsComponent,
    BedroomListComponent,
    BedroomFormComponent,
    RatesListComponent,
    RatesFormComponent,
    RatesPricesComponent,
    AgreementsListComponent,
    AgreementsFormComponent,
    SearchPartnercompaniesComponent,
    HotelbookingComponent,
    HotelbookingsListComponent,
    HotelbookingsFormComponent,
    SearchAvailableBedroomsExtendsComponent,
    GuestsExtendsComponent,
    GuestsListComponent,
    ProformaListComponent,
    ProformaFormComponent,
    HoteltransfersListComponent,
    HotelplaningComponent,
    HotelbookingsDetailComponent,
    ScheduleOpenListComponent,
    VouchersReportComponent,
    HotelbookingsExtendsComponent,
    LoungesListComponent,
    LoungesFormComponent,
    LoungesTablesComponent,
    UserOtpComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    ReactiveFormsModule,
    FormsModule,
    HttpClientModule,
    NgxPaginationModule,
    RouterModule,
    CKEditorModule,
    AutocompleteLibModule,
    NgSelectModule,
    HighchartsChartModule,
    ImageCropperModule,
    DragDropModule,
    FullCalendarModule
  ],
  providers: [
    // { provide: LOCALE_ID, useValue: 'es' },
    { provide: LOCALE_ID, useValue: 'es-PE' },
    ProductService,
    BookingflightsService,
    AuthGuard,
    NavigationTrackerService,
    BranchofficeGuard   
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
